import { css, jsx } from '@emotion/core';
import { Card, Col, Divider, Modal } from 'antd';
import React, { Component } from 'react';
import Media from 'react-media';
import Swiper from 'swiper';

import Button from '@/src/shared/components/Button';
import CustomCard from '@/src/shared/components/Card';
import Carousel from '@/src/shared/components/Carousel';
import Heading from '@/src/shared/components/Heading';
import Sanitize from '@/src/shared/components/Sanitize';
import TopicCardImage from '@/src/shared/components/TopicCardImage';
import { bp, colors, easing, space } from '@/styles';

const { Meta } = Card;

const heights = {
  small: css`
    width: 100%;
    max-width: none;
    @media ${bp.md} {
      width: 32%;
      max-width: 368px;
    }
  `,
  medium: css`
    margin-bottom: 20px;
    height: 375px;
    width: 100%;
    max-width: none;
    &:last-child {
      margin-bottom: 0px;
    }
    @media ${bp.md} {
      max-width: 368px;
      height: 410px;
      margin-bottom: 32px;
      width: 32%;
      display: flex;
      flex-direction: column;
      margin-left: auto;
    }
  `,
};

const style = {
  infographicCard: {
    panelContainer: css`
      overflow: hidden;
      max-height: 319px;
      position: relative;
      width: 100% !important;
      transition: all 0.25s ease-in-out;
      &:hover {
        cursor: pointer;
        .u-panel--effect {
          color: ${colors.primaryRed};
        }
        &:after {
          opacity: 1;
        }
      }
      &:after {
        content: '';
        position: absolute;
        top: 100%;
        width: 100%;
        height: 6px;
        background-color: red;
        transform: translateY(-100%);
        transition: opacity 0.25s ease-in-out;
        opacity: 0;
      }
    `,
    panelBody: css`
      padding: 28px 16px;
      @media ${bp.md} {
        padding: 14px;
      }
      @media ${bp.lg} {
        padding: 28px;
      }
      .u-panel--effect {
        transition: all 0.25s ease-in-out;
      }
    `,
    panelImage: css`
      height: 234px;
      width: 100%;
      object-position: center;
      object-fit: cover;
    `,
    panelTitle: css`
      margin-bottom: 8px;
    `,
    panelDescription: css`
      font-size: 1.6rem;
      line-height: 28px;
      color: ${colors.secondaryGray};
      margin-bottom: 0px;
    `,
  },
  infographics: css`
    display: none;
    @media ${bp.sm} {
      display: block;
    }
    @media ${bp.md} {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;
    }
  `,
  modal: {
    surveyContainer: css`
      /* max-width: 700px; */
    `,
    container: css`
      .ant-modal {
        @media ${bp.md} {
          max-width: calc(100vw - 32px);
          margin-left: auto;
          margin-right: auto;
        }
        @media ${bp.lg} {
          max-width: 978px;
        }
        &-footer {
          @media ${bp.md} {
            padding: 24px;
          }
        }
      }
    `,
    imgs: css`
      display: flex;
      justify-content: space-between;
      margin-bottom: 16px;
      flex-direction: column;
      @media ${bp.sm} {
        flex-direction: row;
      }
    `,
    imgsFigure: css`
      flex-basis: calc(33.333333% - 20px);
      figcaption {
        font-size: 1.2rem;
        line-height: 2rem;
        margin-top: 8px;
      }
    `,
    divider: css`
      margin-left: -24px;
      margin-right: -24px;
      width: calc(100% + 48px);
    `,
    defaultInfographic: css`
      display: flex;
      flex-direction: column;
    `,
    defaultInfographicImage: css`
      margin: auto;
    `,
    title: css`
      font-size: 2rem;
      font-weight: 900;
      line-height: 3.2rem;
      color: ${colors.secondaryGray};
      margin-bottom: 0;
    `,
    description: css`
      font-size: 1.6rem;
      line-height: 2.8rem;
      margin-bottom: 0;
      max-width: 744px;
    `,
  },
  card: css`
    border-radius: 2px;
    box-shadow: 0 6px 12px 0 rgba(35, 32, 32, 0.1);
    position: relative;
    cursor: pointer;
    margin-bottom: 24px;
    @media ${bp.md} {
      margin-bottom: 0;
    }
    &::after {
      width: 100%;
      height: 6px;
      content: '';
      transform-origin: 50% 100%;
      position: absolute;
      bottom: 0;
      left: 0;
      display: block;
      transform: scaleY(0);
      background-color: ${colors.primaryRed};
      transition: transform 0.3s ${easing.easeInOut};
    }
    &:hover {
      box-shadow: 0 11px 12px 0 rgba(35, 32, 32, 0.15);
      &::after {
        transform: scaleY(1);
      }
    }
    .ant-card-cover {
      position: relative;
      &::before {
        content: '';
        display: block;
        z-index: 2;
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: linear-gradient(289.92deg, rgba(238, 238, 238, 0) 0%, #00407d 100%);
      }
    }
  `,
  cardTitle: css`
    color: ${colors.primaryBlue};
    margin-bottom: 8px;
    margin-top: 8px;
  `,
  global: css`
    .infographics-modal {
      .ant-modal {
        @media ${bp.md} {
          max-width: calc(100vw - 32px);
          margin-left: auto;
          margin-right: auto;
        }
        @media ${bp.lg} {
          max-width: 978px;
        }
        &-footer {
          @media ${bp.md} {
            padding: 24px;
          }
        }
      }
    }
  `,
};

const swiperClass = 'infographics';

class Infographics extends Component {
  constructor(props) {
    super(props);
    this.ref_swiper_container = React.createRef();
  }

  state = {
    visible: false,
    selectedIndex: null,
  };

  componentDidMount() {
    // eslint-disable-next-line
    const _S = new Swiper(`.${swiperClass}`, {
      loop: true,
      slidesPerView: 1,
      direction: 'horizontal',
      spaceBetween: 0,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
    });
  }

  handleClick = () => {
    this.setState({ visible: false, selectedIndex: null });
  };

  render() {
    const { blockData, entities } = this.props;
    const { selectedIndex, visible } = this.state;

    let infographicComponents = [];
    if (blockData.Components && Array.isArray(blockData.Components)) {
      infographicComponents = blockData.Components.map(c => entities.Components[c]);
    }

    // const isDataType = selectedIndex && selectedComponent.type === 'infographicData';
    const modalContent = selectedIndex !== null ? entities.Components[selectedIndex] : null;

    const modalContentInner = () => {
      let componentContents = [];

      if (modalContent.Contents && Array.isArray(modalContent.Contents)) {
        componentContents = modalContent.Contents.map(item => entities.Contents[item]);
      }
      if (modalContent.InfographicTypeId === 2) {
        return (
          <div css={style.modal.surveyContainer}>
            <div css={style.modal.imgs}>
              {modalContent.Contents.map(item => (
                <figure key={entities.Contents[item].ImageId} css={style.modal.imgsFigure}>
                  <TopicCardImage imageData={entities.Contents[item]} />
                  <figcaption>{entities.Contents[item].ImageCaption}</figcaption>
                </figure>
              ))}
            </div>
            <div css={style.modal.description}>
              <Sanitize>{modalContent.Paragraph}</Sanitize>
            </div>
          </div>
        );
      }
      return (
        <>
          {componentContents && Array.isArray(componentContents) && componentContents.length ? (
            <div css={style.modal.defaultInfographic}>
              <TopicCardImage
                imageData={componentContents[0]}
                css={style.modal.defaultInfographicImage}
              />
              <Divider css={style.modal.divider} />
              <h4 css={[style.modal.title, space.m.b16]}>{modalContent.Title}</h4>
              <div css={style.modal.description}>
                <Sanitize>{modalContent.Paragraph}</Sanitize>
              </div>
            </div>
          ) : null}
        </>
      );
    };

    return (
      <Media query={bp.md}>
        {isMd => (
          <>
            <Modal
              visible={visible}
              title={
                modalContent &&
                modalContent.InfographicTypeId === 2 && (
                  <h4 css={style.modal.title}>{modalContent.Title}</h4>
                )
              }
              footer={
                modalContent &&
                (modalContent.AddButton === 'Y' || modalContent.AddButton === 'Yes') ? (
                  <a
                    href="https://google.com"
                    target={
                      modalContent.OpenInNewTab === 'Yes' || modalContent.OpenInNewTab === 'Y'
                        ? '_blank'
                        : '_self'
                    }
                  >
                    <Button size="small" icon="double-right">
                      {modalContent.ButtonText}
                    </Button>
                  </a>
                ) : null
              }
              onCancel={this.handleClick}
              width="100%"
              wrapClassName="infographics-modal"
            >
              {modalContent && <div css={modalContent && space.m.t32}>{modalContentInner()}</div>}
            </Modal>
            <div css={style.infographics}>
              {infographicComponents.map((infographic, i) => {
                const { Title, ImageCaption, ImageId, BlockComponentId } = infographic;
                return (
                  <Col
                    xs={24}
                    md={8}
                    key={BlockComponentId}
                    onClick={() => {
                      this.setState({ selectedIndex: BlockComponentId, visible: true });
                    }}
                  >
                    <CustomCard
                      css={[style.infographicCard.panelContainer, heights['medium'], space.m.b24]}
                    >
                      <TopicCardImage imageData={{ ImageId, ImageCaption }} />
                      <div className="o-panel__body" css={style.infographicCard.panelBody}>
                        <Heading
                          css={style.infographicCard.panelTitle}
                          weight="bolder"
                          className="u-panel--effect"
                          type="3"
                          theme="primaryBlue"
                        >
                          <Sanitize>{Title}</Sanitize>
                        </Heading>
                      </div>
                    </CustomCard>
                  </Col>
                );
              })}
            </div>
            {!isMd && (
              <Carousel type="default" containerClassName={swiperClass}>
                {infographicComponents.map((infographic, i) => {
                  const { Title, ImageCaption, ImageId, BlockComponentId } = infographic;
                  return (
                    <Card
                      key={BlockComponentId}
                      cover={
                        <img
                          alt={ImageCaption}
                          src={`${process.env.NEW_API_URL}image/file?area=c&id=${ImageId}`}
                        />
                      }
                      css={style.card}
                      onClick={() => {
                        this.setState({ selectedIndex: BlockComponentId, visible: true });
                      }}
                      bordered={false}
                    >
                      <Meta
                        title={
                          <Heading type={3} weight="bold" css={style.cardTitle}>
                            {Title}
                          </Heading>
                        }
                      />
                    </Card>
                  );
                })}
              </Carousel>
            )}
          </>
        )}
      </Media>
    );
  }
}

Infographics.propTypes = {};

export default Infographics;
